import { useCallback, useEffect, useId, useRef, useState } from "react"
import { DescriptionOutlined } from "@mui/icons-material"
import { Alert, Box, Button, Chip, CircularProgress, List, ListItemButton, ListItemIcon, ListItemText, ListSubheader, Typography } from "@mui/material"
import type { ListItemTextProps, SxProps, Theme } from "@mui/material"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { useAnalytics } from "connected-components/analytics"
import { useScheduledWorkflowDeliverables, useScheduledWorkflowDeliverablesUnreadCount, useScheduledWorkflowRuns } from "resources/scheduled-workflows/hooks"
import type { ScheduledWorkflowRun } from "resources/scheduled-workflows/types"
import { CollapsibleSection, SectionMoreButton, useSidePanelSurface } from "../chatbox/collapsible-section"
import { SUBHEADER_SX } from "../chatbox/side-panel"
import { reportEntryLabel } from "./report-format"
import { runOutcomeWord } from "./latest-report"
import { formatReportTime, viewerTimeZone } from "./schedule-format"

const MESSAGE_SX = { m: 1 } satisfies SxProps<Theme>
const EMPTY_SX = { px: 2, py: 1, color: "text.secondary", typography: "body2" } satisfies SxProps<Theme>
const LOADING_SX = { display: "flex", justifyContent: "center", p: 2 } satisfies SxProps<Theme>
const SHOW_MORE_SX = { justifyContent: "flex-start", textTransform: "none", mx: 1 } satisfies SxProps<Theme>
const UNREAD_PILL_SX = { ml: "auto" } satisfies SxProps<Theme>
const ICON_SX = { minWidth: 0, mr: 1.5 } satisfies SxProps<Theme>
const UNREAD_DOT_SX = { width: 8, height: 8, borderRadius: "50%", bgcolor: "primary.main" } satisfies SxProps<Theme>
const UNREAD_TITLE_SLOT_PROPS = { primary: { sx: { fontWeight: 700 } } } satisfies NonNullable<ListItemTextProps["slotProps"]>

const VIEWER_ZONE = viewerTimeZone()
const EMPTY_RUNS: ScheduledWorkflowRun[] = []

export const REPORTS_VISIBLE_LIMIT = 5

export function ReportsSection({ onOpen }: { onOpen: (deliverableId: string) => void }) {
  const headingId = useId()
  const [isCollapsed, setIsCollapsed] = useState(false)
  const { data, isLoading, isError, isFetchNextPageError, hasNextPage, isFetchingNextPage, fetchNextPage } = useScheduledWorkflowDeliverables()
  const { data: unreadCount } = useScheduledWorkflowDeliverablesUnreadCount()
  const unread = unreadCount?.count ?? 0
  const deliverables = data?.pages.flatMap((page) => page.deliverables) ?? []
  const [isShowingAll, setIsShowingAll] = useState(false)
  const hasOverflow = deliverables.length > REPORTS_VISIBLE_LIMIT
  const [hadOverflow, setHadOverflow] = useState(hasOverflow)
  if (hasOverflow !== hadOverflow) {
    setHadOverflow(hasOverflow)
    if (!hasOverflow) setIsShowingAll(false)
  }
  const visibleDeliverables = isShowingAll ? deliverables : deliverables.slice(0, REPORTS_VISIBLE_LIMIT)
  const hiddenCount = deliverables.length - visibleDeliverables.length
  const listId = `${headingId}-list`
  const toggleRef = useRef<HTMLButtonElement | null>(null)
  const prevIsFetchingNextPage = useRef(false)
  useEffect(() => {
    // When the last page fetch completes, the fetch button unmounts and focus falls to body.
    // Move focus to the Show more/less toggle, which is still mounted.
    if (prevIsFetchingNextPage.current && !isFetchingNextPage && !hasNextPage) {
      toggleRef.current?.focus()
    }
    prevIsFetchingNextPage.current = isFetchingNextPage
  }, [isFetchingNextPage, hasNextPage])
  const loadNextPage = useCallback(() => {
    setIsShowingAll(true)
    void fetchNextPage()
  }, [fetchNextPage])
  const toggleShowAll = useCallback(() => setIsShowingAll((showingAll) => !showingAll), [])
  const analytics = useAnalytics()
  const surface = useSidePanelSurface()
  const toggleCollapsed = useCallback(() => {
    analytics.track(AI_TEAMMATE_EVENTS.THREAD_SECTION_TOGGLED, { section: "reports", collapsed: !isCollapsed, ...(surface == null ? {} : { surface }) })
    setIsCollapsed(!isCollapsed)
  }, [analytics, isCollapsed, surface])

  return (
    <CollapsibleSection
      headingId={headingId}
      label="Reports"
      count={data == null ? undefined : `${deliverables.length}${hasNextPage ? "+" : ""}`}
      adornment={unread > 0 && <Chip size="small" color="primary" sx={UNREAD_PILL_SX} label={unread > 999 ? "999+" : unread} data-testid="reports-unread-pill" />}
      isCollapsed={isCollapsed}
      onToggleCollapse={toggleCollapsed}
      testId="chatbox-reports-section"
    >
      {isError && data == null && <Alert severity="error" sx={MESSAGE_SX}>Couldn&apos;t load your reports. Please try again later.</Alert>}
      {isLoading && (
        <Box sx={LOADING_SX}>
          <CircularProgress size={20} aria-label="Loading reports" />
        </Box>
      )}
      {data != null && deliverables.length === 0 && <Typography sx={EMPTY_SX}>Reports land here once a workflow runs.</Typography>}
      <List id={listId} dense disablePadding>
        {visibleDeliverables.map((deliverable) => (
          <ListItemButton key={deliverable.deliverableId} onClick={(event) => { event.currentTarget.focus(); onOpen(deliverable.deliverableId) }}>
            <ListItemIcon sx={ICON_SX}>
              {deliverable.read
                ? <DescriptionOutlined fontSize="small" data-testid="report-read-icon" />
                : <Box sx={UNREAD_DOT_SX} aria-hidden data-testid="report-unread-dot" />}
            </ListItemIcon>
            <ListItemText primary={reportEntryLabel(deliverable, VIEWER_ZONE)} slotProps={deliverable.read ? undefined : UNREAD_TITLE_SLOT_PROPS} />
          </ListItemButton>
        ))}
      </List>
      {hiddenCount === 0 && hasNextPage && isFetchNextPageError && (
        <Alert severity="error" sx={MESSAGE_SX} action={<Button size="small" onClick={loadNextPage}>Try again</Button>}>Couldn&apos;t load more reports.</Alert>
      )}
      {hiddenCount === 0 && hasNextPage && !isFetchNextPageError && (
        <SectionMoreButton disabled={isFetchingNextPage} onClick={loadNextPage} testId="chatbox-reports-show-more">
          Show more
        </SectionMoreButton>
      )}
      {hasOverflow && (
        <SectionMoreButton
          ref={toggleRef}
          onClick={toggleShowAll}
          testId="chatbox-reports-show-less"
          disclosure={{ isExpanded: isShowingAll, controlsId: listId }}
        >
          {isShowingAll ? "Show less" : `Show ${hiddenCount} more`}
        </SectionMoreButton>
      )}
    </CollapsibleSection>
  )
}

interface RunHistoryProps {
  workflowId: string
  selectedRunId: string | null
  onSelect: (run: ScheduledWorkflowRun) => void
}

export function RunHistory({ workflowId, selectedRunId, onSelect }: RunHistoryProps) {
  const headingId = useId()
  const { data, isLoading, isError, isFetchNextPageError, hasNextPage, isFetchingNextPage, fetchNextPage, refetch } = useScheduledWorkflowRuns(workflowId)
  const runs = data?.pages.flatMap((page) => page.runs) ?? EMPTY_RUNS
  const showMore = useCallback(() => { void fetchNextPage() }, [fetchNextPage])
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(timer)
  }, [])

  return (
    <Box component="section" aria-labelledby={headingId} data-testid="workflow-run-history">
      <ListSubheader id={headingId} component="div" disableSticky sx={SUBHEADER_SX}>All runs</ListSubheader>
      {isError && data == null && <Alert severity="error" sx={MESSAGE_SX} action={<Button size="small" onClick={() => refetch()}>Try again</Button>}>Couldn&apos;t load the runs. Please try again later.</Alert>}
      {isLoading && (
        <Box sx={LOADING_SX}>
          <CircularProgress size={20} aria-label="Loading runs" />
        </Box>
      )}
      {data != null && runs.length === 0 && <Typography sx={EMPTY_SX}>No runs yet</Typography>}
      <List dense disablePadding>
        {runs.map((run) => {
          const outcomeWord = runOutcomeWord(run.outcome)
          const time = formatReportTime(run.createdAt, now, VIEWER_ZONE)
          return (
            <ListItemButton key={run.runId} disabled={run.deliverable == null} selected={run.runId === selectedRunId} onClick={(event) => { event.currentTarget.focus(); onSelect(run) }}>
              <ListItemText primary={outcomeWord == null ? time : `${outcomeWord} · ${time}`} />
            </ListItemButton>
          )
        })}
      </List>
      {isFetchNextPageError && (
        <Alert severity="error" sx={MESSAGE_SX} action={<Button size="small" onClick={showMore}>Try again</Button>}>Couldn&apos;t load more runs.</Alert>
      )}
      {hasNextPage && !isFetchNextPageError && (
        <Button variant="text" size="small" sx={SHOW_MORE_SX} disabled={isFetchingNextPage} onClick={showMore}>
          Show more
        </Button>
      )}
    </Box>
  )
}
